'use client';

/**
 * ImageDisplayField — Display an image inside the form (not an input).
 *
 * Config via field.metadata:
 *   src: string (image URL, or falls back to field.default_value)
 *   alt: string
 *   caption: string
 *   max_height: number (px, default 300)
 *   align: 'left' | 'center' | 'right' (default 'center')
 *   link_url: string (optional click-through URL)
 */

import React from 'react';

import type { FieldComponentField } from '../formFieldTypes';

interface ImageDisplayFieldProps {
  field: FieldComponentField;
  isDark: boolean;
}

export const ImageDisplayField: React.FC<ImageDisplayFieldProps> = ({ field, isDark }) => {
  const meta = field.metadata as {
    src?: string;
    alt?: string;
    caption?: string;
    max_height?: number;
    align?: 'left' | 'center' | 'right';
    link_url?: string;
  } | undefined;
  const src = meta?.src || (typeof field.default_value === 'string' ? field.default_value : '');
  const alt = meta?.alt || field.label || '';
  const caption = meta?.caption || '';
  const maxHeight = meta?.max_height || 300;
  const align = meta?.align || 'center';
  const linkUrl = meta?.link_url || '';

  if (!src) {
    return (
      <div className={`col-span-full text-sm p-3 rounded ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
        No image URL configured for &quot;{field.label}&quot;
      </div>
    );
  }

  const justifyClass =
    align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';

  const img = (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt}
      className="rounded-lg shadow-sm object-contain"
      style={{ maxHeight: `${maxHeight}px` }}
    />
  );

  return (
    <div className={`col-span-full flex flex-col items-center`}>
      <div className={`flex ${justifyClass} w-full`}>
        {linkUrl ? (
          <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
            {img}
          </a>
        ) : (
          img
        )}
      </div>
      {caption && (
        <p className={`mt-2 text-xs text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {caption}
        </p>
      )}
    </div>
  );
};
